import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth, user as userApi } from '../../utils/api';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n } = useTranslation();
  const authCheckInProgress = useRef(false);

  // Define public routes that don't require authentication
  const publicRoutes = ['/', '/events-landing', '/login', '/register', '/team', '/sponsors'];
  
  const isPublicRoute = useCallback(() => {
    return publicRoutes.includes(location.pathname);
  }, [location.pathname]);

  const checkAuth = useCallback(async () => {
    // Skip auth check entirely for public routes
    if (isPublicRoute()) {
      setLoading(false);
      return;
    }
    
    if (authCheckInProgress.current) return;
    
    try {
      authCheckInProgress.current = true;
      setLoading(true);
      
      const response = await userApi.getProfile();
      
      if (response.user) {
        const userData = {
          ...response.user,
          _id: response.user._id.toString()
        };
        setUser(userData);
        
        if (response.user.language) {
          await i18n.changeLanguage(response.user.language);
        }
      } else {
        setUser(null);
        // Only redirect to login if not on a public route
        if (!isPublicRoute()) {
          navigate('/login');
        }
      }
    } catch (err) {
      // Only clear user and redirect if it's an auth error and not on a public route
      if ((err.response?.status === 401 || err.response?.status === 403) && !isPublicRoute()) {
        setUser(null);
        navigate('/login');
      }
    } finally {
      authCheckInProgress.current = false;
      setLoading(false);
    }
  }, [i18n, navigate, isPublicRoute]);

  // Run auth check on mount and when dependencies change
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    try {
      setLoading(true);
      setError(null);
      const response = await auth.login(email, password);
      
      // Ensure user ID is properly set
      const userData = {
        ...response.user,
        _id: response.user._id.toString()
      };
      setUser(userData);
      
      if (response.user?.language) {
        await i18n.changeLanguage(response.user.language);
      }
      
      const redirectTo = sessionStorage.getItem('redirectTo') || '/dashboard';
      sessionStorage.removeItem('redirectTo');
      navigate(redirectTo);
      
      return { success: true };
    } catch (err) {
      setError(err.response?.data?.message || 'An error occurred during login');
      return { success: false };
    } finally {
      setLoading(false);
    }
  }, [navigate, i18n]);

  const register = useCallback(async (email, password, username, accessCode) => {
    try {
      setLoading(true);
      setError(null);
      const response = await auth.register(email, password, username, accessCode);
      
      // Ensure user ID is properly set
      const userData = {
        ...response.user,
        _id: response.user._id.toString()
      };
      setUser(userData);
      
      if (response.user?.language) {
        await i18n.changeLanguage(response.user.language);
      }
      
      navigate('/dashboard');
      return { success: true };
    } catch (err) {
      setError(err.response?.data?.message || 'An error occurred during registration');
      return { success: false };
    } finally {
      setLoading(false);
    }
  }, [navigate, i18n]);

  const logout = useCallback(async () => {
    try {
      await auth.logout();
      setUser(null);
      await i18n.changeLanguage(i18n.options.fallbackLng);
      navigate('/login');
    } catch (err) {
      console.error('Logout error:', err);
    }
  }, [navigate, i18n]);

  const value = {
    user,
    setUser,
    loading,
    error,
    login,
    register,
    logout,
  };

  if (loading && !user) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        Loading...
      </div>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider; 